import { FaChevronUp, FaChevronDown, FaSearch, FaShoppingCart, FaHeart } from 'react-icons/fa';
import { RxCross2 } from 'react-icons/rx';
import { AiOutlinePlus, AiOutlineMinus } from 'react-icons/ai';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CrispChatLoader from './utils/crisp';
import InstallPrompt from './utils/InstallPromptToMobile';

import { getIcon } from 'utils/icons';
import {
    getAlreadyOrderedProducts,
    getAutoOrderConditions,
    getPaginatedAutoOrderProducts,
    sendAutoOrder,
    updateAutoOrder,
    getLastOrder,
    checkOrderForDate,
    toggleLikeProduct,
    trackProductView,
} from 'interfaces/autoOrder';
import InputField from 'components/fields/InputField';
import ProductCard from './ProductCard';
import ProductLoadingCard from './ProductLoadingCard';
import { useDisclosure } from '@chakra-ui/hooks';
import AnnounceModal from 'views/admin/main/ecommerce/commandPage/default-toola/AnnounceModal';
import Header from '../default-toola/Header';
import ProductViewModal from '../default-toola/ProductViewModal';
import ExistingOrderModal from './ExistingOrderModal';
import { icons } from 'views/stock/updateProduct/components/CreateCategory';

const CART_STORAGE_KEY = 'toola_b2b_cart';

const CommandInfo = (props: { haveMultipleSupliers: boolean; isSelfPreview?: boolean }) => {
    const { haveMultipleSupliers, isSelfPreview } = props;

    const nav = useNavigate();
    const [autoOrderProducts, setAutoOrderProducts] = useState([]);
    // Unscoped snapshot (no category/search filter) kept separately so the
    // checkout upsell always recommends from the whole catalog, even if
    // autoOrderProducts is currently narrowed to a category or a search
    const [recommendationPool, setRecommendationPool] = useState<any[]>([]);
    const [alreadyOrderedProducts, setAlreadyOrderedProducts] = useState([]);
    const [likedProductIds, setLikedProductIds] = useState<string[]>([]);
    const [autoOrderProductsMeta, setAutoOrderProductsMeta] = useState(null);
    const [lastOrder, setLastOrder] = useState<any>(null);
    const [loadingLastOrder, setLoadingLastOrder] = useState(false);
    const [lastOrderAdded, setLastOrderAdded] = useState(false);
    const [newOrder, setNewOrder] = useState<any>(() => {
        const savedCart = localStorage.getItem(CART_STORAGE_KEY);
        if (savedCart) {
            try {
                return JSON.parse(savedCart);
            } catch (e) {
                console.error('Erreur lors du chargement du panier:', e);
            }
        }
        return { items: [], totalPrice: 0 };
    });
    const [search, setSearch] = useState('');

    const [searchQuery, setSearchQuery] = useState('');
    const [loadingMore, setLoadingMore] = useState(false);
    const isFirstSearchRun = useRef(true);
    const isFirstCategoryRun = useRef(true);
    // Bumped on every reset (initial load / new search) so a page fetch that
    // resolves after a newer search has started doesn't get appended onto it
    const fetchSessionRef = useRef(0);
    // Every product (and child) ever fetched, keyed by id - kept across
    // category/search resets so cart items from a category the user has since
    // navigated away from can still be resolved (packaging, price, unity...)
    const productsCacheRef = useRef<Record<string, any>>({});
    const cacheProducts = (products: any[]) => {
        for (const p of products) {
            productsCacheRef.current[p.id] = p;
            if (p.children && p.children.length > 0) {
                for (const c of p.children) {
                    productsCacheRef.current[c.id] = c;
                }
            }
        }
    };
    const findCachedProduct = (productId: string) => productsCacheRef.current[productId];
    const [dateError, setDateError] = useState(null);
    const [page, setPage] = useState(1);
    const [disableSendButton, setDisableSendButton] = useState(false);
    const isSubmitting = useRef(false);
    const isFullyLoaded = autoOrderProductsMeta !== null;
    const [showOrderSummary, setShowOrderSummary] = useState(false);
    // editingOrderId is persisted inside the saved cart under the key _editingOrderId
    // so a page refresh doesn't lose the edit context.
    const [productsPerLoad, setProductsPerLoad] = useState(0);
    const [companyInfos, setCompanyInfos] = useState<any>(null);
    const [conditions, setConditions] = useState<any>(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [category, setCategory] = useState('all');
    const [subCategory, setSubCategory] = useState('all');
    const [subFamily, setSubFamily] = useState('all');
    const [selectedPackagings, setSelectedPackagings] = useState<{[key: string]: any}>({});
    const [openCommentFields, setOpenCommentFields] = useState<{[key: string]: boolean}>({});
    const [expandedReplacements, setExpandedReplacements] = useState<{[key: string]: boolean}>({});
    const [showHeader, setShowHeader] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [sortBy, setSortBy] = useState<'default' | 'az' | 'price-asc' | 'price-desc'>('default');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

    const [selectedProductId, setSelectedProductId] = useState(0);
    const [confirmPage, setConfirmPage] = useState(false);

    const [ownerId, setOwnerId] = useState<string | null>(null);
    const [contactEmail, setContactEmail] = useState<string>('');
    const [showEmailSelector, setShowEmailSelector] = useState(false);
    const [editingOrderId, setEditingOrderId] = useState<string | null>(() => {
        try {
            const savedCart = localStorage.getItem(CART_STORAGE_KEY);
            if (savedCart) {
                const parsed = JSON.parse(savedCart);
                return parsed._editingOrderId || null;
            }
        } catch {}
        return null;
    });

    const {
        isOpen: isOpenProductView,
        onOpen: onOpenProductView,
        onClose: onCloseProductView,
    } = useDisclosure();
    
    const {
        isOpen: isOpenExistingOrder,
        onOpen: onOpenExistingOrder,
        onClose: onCloseExistingOrder,
    } = useDisclosure();
    
    const [existingOrders, setExistingOrders] = useState<any[]>([]);
    const [pendingDate, setPendingDate] = useState<string>('');

    const productsPerScroll = 36;

    // Records a product view for sales-prep analytics once the detail modal
    // is actually open (not on every setSelectedProductId call).
    useEffect(() => {
        if (isOpenProductView && selectedProductId) {
            trackProductView(selectedProductId as any);
        }
    }, [isOpenProductView, selectedProductId]);

    useEffect(() => {
        const toSave = editingOrderId
            ? { ...newOrder, _editingOrderId: editingOrderId }
            : newOrder;
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(toSave));
    }, [newOrder, editingOrderId]);

    const handleChange = (e: any) => {
        setNewOrder((prevCustomer: any) => ({
            ...prevCustomer,
            [e.target.id]: e.target.value,
        }));
    };

    const handleDateChange = async (e: any) => {
        const today = new Date();
        const date = new Date(e.target.value);
        const day = date
            .toLocaleDateString('en-US', { weekday: 'long' })
            .toLowerCase();

        date.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        if (conditions && conditions.blockedDays.includes(day)) {
            let error = 'Livraisons indisponible le ';

            conditions.blockedDaysFr.forEach(
                (blockedDay: any, index: number) => {
                    if (index === 0) error += blockedDay;
                    else if (index === conditions.blockedDaysFr.length - 1)
                        error += ' et ' + blockedDay;
                    else error += ', ' + blockedDay;
                },
            );
            setDateError(error);
            handleChange({ target: { id: 'deliveryDate', value: '' } });
            return;
        }

        if (conditions && conditions.deliveryDelay !== 0) {
            const deliveryDelay = conditions.deliveryDelay;
            const deliveryDate = new Date(e.target.value);

            const dateOrderAvailable = new Date();
            dateOrderAvailable.setHours(0, 0, 0, 0);
            dateOrderAvailable.setDate(
                dateOrderAvailable.getDate() + deliveryDelay,
            );

            if (dateOrderAvailable > deliveryDate) {
                setDateError(
                    `La date de livraison doit être au moins dans ${deliveryDelay} jours`,
                );
                handleChange({ target: { id: 'deliveryDate', value: '' } });
                return;
            }
        }

        if (date < today) {
            setDateError('La date de livraison ne peut pas être dans le passé');
            handleChange({ target: { id: 'deliveryDate', value: '' } });
            return;
        }
        
        setDateError(null);
        
        // Check if there are already orders for this date
        const checkResult = await checkOrderForDate(e.target.value);
        if (checkResult && checkResult.hasOrders && checkResult.orders.length > 0) {
            // In edit mode, exclude the order being edited from the list
            const otherOrders = editingOrderId
                ? checkResult.orders.filter((o: any) => o._id !== editingOrderId)
                : checkResult.orders;

            if (otherOrders.length > 0) {
                setPendingDate(e.target.value);
                setExistingOrders(otherOrders);
                onOpenExistingOrder();
            } else {
                handleChange(e);
            }
        } else {
            handleChange(e);
        }
    };
    
    const confirmDateChange = () => {
        handleChange({ target: { id: 'deliveryDate', value: pendingDate } });
        setPendingDate('');
        setExistingOrders([]);
    };

    const handleEditOrder = (order: any) => {
        // Base: items from the existing order
        const existingItems: any[] = (order.items || []).map((item: any) => ({
            productId: item.productId,
            quantity: item.quantity,
            unity: item.unity,
            name: item.name,
            price: item.price,
            img: getIcon(item.name, 'product'),
            ...(item.packagingRef && {
                packagingRef: item.packagingRef,
                packagingUnity: item.packagingUnity,
                packagingWeight: item.packagingWeight,
                packagingBillByBaseUnit: item.packagingBillByBaseUnit || false,
                packagingUseReprocessedPrice: item.packagingUseReprocessedPrice || false,
            }),
            ...(item.comment && { comment: item.comment }),
        }));

        // Merge current cart items (products added before clicking "Modifier")
        const mergedItems = [...existingItems];
        for (const currentItem of newOrder.items || []) {
            const alreadyIn = mergedItems.find(
                (i: any) =>
                    i.productId === currentItem.productId &&
                    (i.packagingRef ?? null) === (currentItem.packagingRef ?? null)
            );
            if (alreadyIn) {
                // Same product + same packaging: just add the quantity
                alreadyIn.quantity += currentItem.quantity;
            } else {
                // Different packaging (or no packaging on one side): add as a distinct line
                // If an existing line has the same productId but a different packaging,
                // we still push the cart item separately so no data is lost.
                mergedItems.push({ ...currentItem });
            }
        }

        const totalPrice = mergedItems.reduce(
            (sum: number, i: any) => sum + (i.price || 0) * (i.quantity || 0),
            0
        );

        // Hydrate selectedPackagings from the merged items so the UI reflects
        // the actual packaging stored in each cart line (not the default one).
        const packagingUpdates: { [key: string]: any } = {};
        for (const item of mergedItems) {
            if (!item.packagingRef) continue;

            const product = findCachedProduct(item.productId);
            if (!product) continue;

            const matchedPkg = (product.packagings || []).find(
                (pkg: any) =>
                    pkg.ref === item.packagingRef ||
                    (pkg.unity === item.packagingUnity &&
                        pkg.weight === item.packagingWeight)
            );
            if (matchedPkg) {
                packagingUpdates[item.productId] = matchedPkg;
            }
        }
        if (Object.keys(packagingUpdates).length > 0) {
            setSelectedPackagings((prev: any) => ({ ...prev, ...packagingUpdates }));
        }

        const deliveryDateValue = order.deliveryDate
            ? new Date(order.deliveryDate).toISOString().split('T')[0]
            : '';

        setNewOrder({
            items: mergedItems,
            totalPrice,
            deliveryDate: deliveryDateValue,
            customerNotes: order.customerNotes || '',
        });
        setEditingOrderId(order._id);
        // Pre-fill the date
        handleChange({ target: { id: 'deliveryDate', value: deliveryDateValue } });
        setPendingDate('');
        setExistingOrders([]);
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // reset === true starts a fresh list (initial load, a new search term, or a new category);
    // reset === false appends the next page to the currently loaded list (infinite scroll)
    const fetchProducts = async (
        pageToLoad: number,
        searchTerm: string,
        categoryFilter: string,
        subCategoryFilter: string,
        reset: boolean,
        subFamilyFilter: string = '',
    ) => {
        // Requests started under an older session (a search reset happened since)
        // are discarded when they resolve, so they can't corrupt the current list
        const sessionId = reset ? ++fetchSessionRef.current : fetchSessionRef.current;

        if (reset) {
            setAutoOrderProductsMeta(null);
        } else {
            setLoadingMore(true);
        }

        const res = await getPaginatedAutoOrderProducts(
            pageToLoad,
            productsPerScroll,
            searchTerm,
            categoryFilter,
            subCategoryFilter,
            subFamilyFilter,
        );
        if (sessionId !== fetchSessionRef.current) {
            // A newer search started while this request was in flight; drop it
            setLoadingMore(false);
            return;
        }
        if (res) {
            if (reset) {
                setCompanyInfos({
                    company: res.company,
                    customerCompany: res.customerCompany,
                    customerEmail: res.customerEmail,
                    additionalMails: res.additionalMails || [],
                    logo: res.companyLogoUrl,
                    hidePricesOnShop: res.hidePricesOnShop,
                    showProductDetailsOnShop: res.showProductDetailsOnShop,
                    contact: res.contact,
                    announcements: res.announcements,
                    categories: res.categories,
                    subCategories: res.subCategories || [],
                    subFamilies: res.subFamilies || [],
                    customerId: res.customerId,
                });

                if (res.companyOwnerId) {
                    setOwnerId(res.companyOwnerId);
                }

                // Load saved contactEmail or prompt if multiple emails
                const allMails = [res.customerEmail, ...(res.additionalMails || [])];
                const savedEmail = localStorage.getItem(`b2b_contact_${res.customerId}`);
                if (savedEmail && allMails.includes(savedEmail)) {
                    setContactEmail(savedEmail);
                } else if (allMails.length > 1) {
                    setContactEmail('');
                    setShowEmailSelector(true);
                } else {
                    setContactEmail(res.customerEmail);
                }

                if (res.announcements) onOpen();
                setProductsPerLoad(productsPerScroll);
            }

            const products = res.products;
            cacheProducts(products);
            setAutoOrderProducts((prev) => (reset ? [...products] : [...prev, ...products]));
            setPage(pageToLoad + 1);
            setAutoOrderProductsMeta(res.meta);
            if (reset && !categoryFilter && !subCategoryFilter && !searchTerm) {
                setRecommendationPool(products);
            }

            // Auto-select the default (or first) b2bOnly packaging for products that have some
            setSelectedPackagings((prev) => {
                const updates: {[key: string]: any} = {};
                for (const p of products) {
                    const b2bOnlyPkgs = (p.packagings || []).filter((pkg: any) => pkg.b2bOnly);
                    if (b2bOnlyPkgs.length > 0 && !prev[p.id]) {
                        const defaultPkg = b2bOnlyPkgs.find((pkg: any) => pkg.isDefault) || b2bOnlyPkgs[0];
                        updates[p.id] = defaultPkg;
                    }
                }
                return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
            });
        }
        setLoadingMore(false);
    };

    const sendOrder = async () => {
        // Read-only preview: the owner is browsing their own shop, no real order can go out.
        if (isSelfPreview) return;

        // Blocked customer: no order (new or edited) can go out, but browsing stays allowed.
        if (conditions?.isBlocked) return;

        // Protection immediatly with ref and state to prevent multiple clicks
        if (isSubmitting.current || disableSendButton) {
            console.warn('Soumission déjà en cours, requête ignorée');
            return;
        }

        // Block immediately (synchronous)
        isSubmitting.current = true;
        setDisableSendButton(true);

        try {
            // Prepare the data to send
            const data = { ...newOrder };
            data.contactEmail = contactEmail || companyInfos?.customerEmail;
            data.items = data.items
                .filter((i: any) => i.quantity > 0 && i.quantity !== '')
                .map((i: any) => {
                    const { img, packagingUseReprocessedPrice, ...itemWithoutImg } = i;
                    return itemWithoutImg;
                });
            
            // If editing an existing order, update it instead of creating a new one
            let res: any;
            if (editingOrderId) {
                res = await updateAutoOrder(editingOrderId, data);
            } else {
                res = await sendAutoOrder(data);
            }
            
            if (res) {
                localStorage.removeItem(CART_STORAGE_KEY);
                setNewOrder({ items: [], totalPrice: 0 });
                setEditingOrderId(null);
                // _editingOrderId is embedded in the cart key, already cleared above

                if (res.orderId) nav(`/command/success?id=${res.orderId}`);
                else nav('/command/success');
            } else {
                // Reactivate only in case of failure
                isSubmitting.current = false;
                setTimeout(() => {
                    setDisableSendButton(false);
                }, 3000);
            }
        } catch (error) {
            console.error('Erreur lors de l\'envoi de la commande:', error);
            isSubmitting.current = false;
            setDisableSendButton(false);
        }
    };

    const searchProduct = (product: any, search: string) => {
        for (const key in product)
            if (
                product[key] &&
                typeof product[key] === 'string' &&
                product[key]
                    .toLocaleLowerCase()
                    .includes(search.toLocaleLowerCase())
            )
                return true;
        return false;
    };

    // Indicative display price: price * weight when useReprocessedPrice, otherwise raw price
    // item.price always stays the real raw packaging price (used for order submission)
    const getItemDisplayPrice = (item: any): number => {
        if (item.packagingUseReprocessedPrice && item.packagingWeight > 0) {
            return item.price * item.packagingWeight;
        }
        return item.price;
    };

    const addNewItem = (
        productId: string,
        name: string,
        quantity: number,
    ) => {
        let updatedItems: any = [];
        const alreadyExist = newOrder.items.find(
            (i: any) => i.productId === productId,
        );
        
        let price: any = null;
        let unity: any = null;

        const cachedProduct = findCachedProduct(productId);
        if (cachedProduct) {
            price = cachedProduct.price;
            unity = cachedProduct.unity;
        }

        if (price === null || unity === null) {
            console.error('Product not found:', productId);
            return;
        }
        
        // Check if a packaging is selected
        const selectedPackaging = selectedPackagings[productId];
        if (selectedPackaging) {
            price = parseFloat(selectedPackaging.price);
            // Don't change unity - it should stay as base unity
        }
    
        const img = getIcon(name, 'product');
    
        if (alreadyExist) {
            updatedItems = newOrder.items.map((item: any) => {
                if (item.productId === productId) {
                    return {
                        ...item,
                        quantity: item.quantity + quantity,
                        price,
                        // unity stays the same (base unity)
                        ...(selectedPackaging && {
                            packagingRef: selectedPackaging.ref,
                            packagingUnity: selectedPackaging.unity,
                            packagingWeight: selectedPackaging.weight,
                            packagingBillByBaseUnit: selectedPackaging.billByBaseUnit || false,
                            packagingUseReprocessedPrice: selectedPackaging.useReprocessedPrice || false,
                        }),
                    };
                }
                return item;
            });
        } else {
            updatedItems = [...newOrder.items];
            updatedItems.push({
                productId: productId,
                quantity: quantity,
                unity: unity, // Base unity
                img: img,
                name,
                price,
                ...(selectedPackaging && {
                    packagingRef: selectedPackaging.ref,
                    packagingUnity: selectedPackaging.unity,
                    packagingWeight: selectedPackaging.weight,
                    packagingBillByBaseUnit: selectedPackaging.billByBaseUnit || false,
                    packagingUseReprocessedPrice: selectedPackaging.useReprocessedPrice || false,
                }),
            });
        }
    
        setNewOrder((prevCustomer: any) => ({
            ...prevCustomer,
            items: updatedItems,
            totalPrice: prevCustomer.totalPrice + price * quantity,
        }));
    };

    const removeItem = (productId: string) => {
        const itemToRemove = newOrder.items.find(
            (i: any) => i.productId === productId,
        );
        if (!itemToRemove) return;

        let totalPrice = newOrder.totalPrice;

        if (itemToRemove.quantity >= 1)
            totalPrice -= itemToRemove.price * itemToRemove.quantity;

        let updatedItems = newOrder.items.map((item: any) => {
            if (item.productId === productId) {
                return {
                    ...item,
                    quantity: item.quantity - itemToRemove.quantity,
                };
            }
            return item;
        });

        updatedItems = updatedItems.filter((item: any) => item.quantity > 0);

        setNewOrder((prevCustomer: any) => ({
            ...prevCustomer,
            items: updatedItems,
            totalPrice,
        }));
    };

    const handleQuantityChange = (quantity: number, id: string) => {
        if (quantity === 0) {
            return removeItem(id);
        }

        const updatedItem = newOrder.items.find((i: any) => i.productId === id);

        let totalPrice =
            newOrder.totalPrice - updatedItem.quantity * updatedItem.price;
        totalPrice += quantity * updatedItem.price;

        setNewOrder((prevCustomer: any) => ({
            ...prevCustomer,
            items: prevCustomer.items.map((item: any) => {
                if (item.productId === id) {
                    return {
                        ...item,
                        quantity: quantity,
                    };
                }
                return item;
            }),
            totalPrice: totalPrice,
        }));
    };

    const handlePackagingChangeInCart = (productId: string, packaging: any) => {
        const item = newOrder.items.find((i: any) => i.productId === productId);
        if (!item) {
            console.log('Item not found:', productId);
            return;
        }

        // Find the product to get base price
        const product = findCachedProduct(productId);
        if (!product) {
            console.log('Product not found:', productId);
            return;
        }

        const oldPrice = item.price;
        const newPrice = packaging ? parseFloat(packaging.price) : product.price;
        const newUnity = packaging ? packaging.unity : product.unity;

        console.log('Packaging change:', {
            productId,
            packaging,
            oldPrice,
            newPrice,
            oldUnity: item.unity,
            newUnity,
        });

        // Calculate new total price
        const totalPrice = newOrder.totalPrice - (oldPrice * item.quantity) + (newPrice * item.quantity);

        setNewOrder((prevOrder: any) => ({
            ...prevOrder,
            items: prevOrder.items.map((i: any) => {
                if (i.productId === productId) {
                    if (packaging) {
                        return {
                            ...i,
                            price: newPrice,
                            // unity stays the same (base unity, not changed)
                            packagingRef: packaging.ref,
                            packagingUnity: packaging.unity,
                            packagingWeight: packaging.weight,
                            packagingBillByBaseUnit: packaging.billByBaseUnit || false,
                            packagingUseReprocessedPrice: packaging.useReprocessedPrice || false,
                        };
                    } else {
                        // Reset to base product - remove packaging fields
                        const { packagingRef, packagingUnity, packagingWeight, packagingBillByBaseUnit, packagingUseReprocessedPrice, ...itemWithoutPackaging } = i;
                        return {
                            ...itemWithoutPackaging,
                            price: newPrice,
                            // unity stays the same (already base unity)
                        };
                    }
                }
                return i;
            }),
            totalPrice,
        }));
    };

    const handleCommentChange = (productId: string, comment: string) => {
        setNewOrder((prevOrder: any) => ({
            ...prevOrder,
            items: prevOrder.items.map((i: any) => {
                if (i.productId === productId) {
                    return {
                        ...i,
                        comment: comment,
                    };
                }
                return i;
            }),
        }));
    };

    const toggleCommentField = (productId: string) => {
        setOpenCommentFields(prev => ({
            ...prev,
            [productId]: !prev[productId]
        }));
    };

    const fetchAlreadyOrderedProducts = async () => {
        const res = await getAlreadyOrderedProducts();
        if (res) {
            // Support both old format (plain array) and new format ({alreadyOrdered, likedProducts})
            if (Array.isArray(res)) {
                setAlreadyOrderedProducts(res);
            } else {
                setAlreadyOrderedProducts(res.alreadyOrdered || []);
                setLikedProductIds(res.likedProducts || []);
            }
        }
    };

    const handleToggleLike = async (productId: string) => {
        // Optimistic update
        const isLiked = likedProductIds.includes(productId);
        if (isLiked) {
            setLikedProductIds((prev) => prev.filter((id) => id !== productId));
        } else {
            setLikedProductIds((prev) => [...prev, productId]);
        }
        const res = await toggleLikeProduct(productId);
        if (res) {
            setLikedProductIds(res.likedProducts || []);
        } else {
            // Rollback on error
            setLikedProductIds((prev) =>
                isLiked ? [...prev, productId] : prev.filter((id) => id !== productId)
            );
        }
    };

    const fetchConditions = async () => {
        const res = await getAutoOrderConditions();
        if (res) setConditions(res);
    };

    const fetchLastOrder = async () => {
        const res = await getLastOrder();
        console.log('Last order response:', res);
        if (res && res.data) {
            console.log('Last order data:', res.data);
            setLastOrder(res.data);
        }
    };

    const addLastOrderToCart = () => {
        console.log('addLastOrderToCart called');
        console.log('lastOrder:', lastOrder);
        console.log('autoOrderProducts:', autoOrderProducts);
        
        if (!lastOrder || !lastOrder.items || lastOrder.items.length === 0) {
            console.log('No last order or items');
            return;
        }

        setLoadingLastOrder(true);
        
        let totalPrice = newOrder.totalPrice;
        const updatedItems = [...newOrder.items];

        lastOrder.items.forEach((orderItem: any) => {
            const product = findCachedProduct(orderItem.productId);
            if (!product) return; // Product not found

            const existingItem = updatedItems.find(
                (i: any) => i.productId === orderItem.productId
            );

            if (existingItem) {
                existingItem.quantity += orderItem.quantity;
                totalPrice += product.price * orderItem.quantity;
            } else {
                const img = getIcon(product.name, 'product');
                updatedItems.push({
                    productId: orderItem.productId,
                    quantity: orderItem.quantity,
                    unity: product.unity,
                    img: img,
                    name: product.name,
                    price: product.price,
                });
                totalPrice += product.price * orderItem.quantity;
            }
        });

        setNewOrder({
            ...newOrder,
            items: updatedItems,
            totalPrice: totalPrice,
        });

        setLoadingLastOrder(false);
        setLastOrderAdded(true);
    };

    const getOrderCount = (productId: any) => {
        if (!Array.isArray(alreadyOrderedProducts)) return 0;
        const sum = alreadyOrderedProducts.find((p: any) => p._id === productId)
            ?.count;
        return sum ? sum : 0;
    };

    const productsHaveFav = () => {
        if (likedProductIds.length > 0) return true;
        return Object.values(productsCacheRef.current).some((p: any) => getOrderCount(p.id) > 0);
    };

    // 'all' and 'fav' aren't real backend categories: 'all' means no scoping,
    // and 'fav' needs the whole catalog client-side (favorites can be in any category)
    const backendCategoryFilter = category !== 'all' && category !== 'fav' ? category : '';
    const backendSubCategoryFilter = backendCategoryFilter && subCategory !== 'all' ? subCategory : '';
    const backendSubFamilyFilter = backendSubCategoryFilter && subFamily !== 'all' ? subFamily : '';

    useEffect(() => {
        fetchProducts(1, '', '', '', true, '');
        fetchAlreadyOrderedProducts();
        fetchConditions();
        fetchLastOrder();
    }, []);

    // Debounce the search input before querying the backend so the whole
    // catalog is searched (not just the products already loaded on screen)
    useEffect(() => {
        const timeout = setTimeout(() => {
            setSearchQuery(search.trim());
        }, 350);
        return () => clearTimeout(timeout);
    }, [search]);

    // A new search term always needs a fresh fetch (scoped to whichever
    // category is currently selected, or the whole catalog if none is)
    useEffect(() => {
        if (isFirstSearchRun.current) {
            isFirstSearchRun.current = false;
            return;
        }
        fetchProducts(1, searchQuery, backendCategoryFilter, backendSubCategoryFilter, true, backendSubFamilyFilter);
    }, [searchQuery]);

    // Selecting a category/sub-category always refetches scoped to it, so every
    // product in that category is available right away instead of trickling in
    // as the user scrolls through the whole (unfiltered) catalog.
    // Depends on the derived backend filters (not the raw category/subCategory)
    // so switching between 'all' and 'fav' - both unscoped - doesn't refetch
    useEffect(() => {
        if (isFirstCategoryRun.current) {
            isFirstCategoryRun.current = false;
            return;
        }
        fetchProducts(1, searchQuery, backendCategoryFilter, backendSubCategoryFilter, true, backendSubFamilyFilter);
    }, [backendCategoryFilter, backendSubCategoryFilter, backendSubFamilyFilter]);

    // Infinite scroll: load the next page once the user nears the bottom
    useEffect(() => {
        const handleInfiniteScroll = () => {
            if (confirmPage || loadingMore || !autoOrderProductsMeta) return;
            if (autoOrderProducts.length >= autoOrderProductsMeta.total) return;

            const nearBottom =
                window.innerHeight + window.scrollY >=
                document.documentElement.scrollHeight - 600;
            if (nearBottom) {
                fetchProducts(page, searchQuery, backendCategoryFilter, backendSubCategoryFilter, false, backendSubFamilyFilter);
            }
        };

        window.addEventListener('scroll', handleInfiniteScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleInfiniteScroll);
    }, [confirmPage, loadingMore, autoOrderProductsMeta, autoOrderProducts.length, page, searchQuery, backendCategoryFilter, backendSubCategoryFilter, backendSubFamilyFilter]);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            
            // Only hide/show after scrolling past 100px
            if (currentScrollY < 100) {
                setShowHeader(true);
            } else {
                // Scrolling down - hide header
                if (currentScrollY > lastScrollY) {
                    setShowHeader(false);
                }
                // Scrolling up - show header
                else if (currentScrollY < lastScrollY) {
                    setShowHeader(true);
                }
            }
            
            setLastScrollY(currentScrollY);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollY]);

    const visibleProducts = isFullyLoaded
        ? autoOrderProducts
              .filter((p: any) => searchProduct(p, search))
              .filter((p: any) =>
                  category === 'all' || category === 'fav'
                      ? true
                      : p.category === category,
              )
              .filter((p: any) => {
                  // Filter by sub-category only if a category is selected and subCategory is not 'all'
                  if (category === 'all' || category === 'fav' || subCategory === 'all') {
                      return true;
                  }
                  return p.productSubCategory === subCategory;
              })
              .filter((p: any) => {
                  // Filter by sub-family only if a sub-category is selected and subFamily is not 'all'
                  if (category === 'all' || category === 'fav' || subCategory === 'all' || subFamily === 'all') {
                      return true;
                  }
                  return p.productSubFamily === subFamily;
              })
              .filter((p: any) =>
                  category === 'fav'
                      ? likedProductIds.includes(p.id) || getOrderCount(p.id) > 0
                      : true,
              )
              .sort((a: any, b: any) => {
                  if (sortBy === 'az') return a.name.localeCompare(b.name);
                  if (sortBy === 'price-asc') return (a.price || 0) - (b.price || 0);
                  if (sortBy === 'price-desc') return (b.price || 0) - (a.price || 0);
                  return getOrderCount(b.id) - getOrderCount(a.id);
              })
        : [];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50">
            {ownerId && companyInfos && (
                <CrispChatLoader 
                    ownerId={ownerId}
                    userEmail={companyInfos.customerEmail}
                    userName={companyInfos.customerCompany}
                    userId={companyInfos.customerId}
                />
            )}
            {/* Install PWA */}
            <InstallPrompt />

            {/* Email selector modal */}
            {showEmailSelector && companyInfos && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl p-6 mx-4 max-w-sm w-full">
                        <h2 className="text-lg font-semibold text-navy-700 mb-1">Qui commande ?</h2>
                        <p className="text-sm text-gray-500 mb-4">Sélectionnez votre adresse email pour identifier votre commande.</p>
                        <div className="flex flex-col gap-2">
                            {[companyInfos.customerEmail, ...(companyInfos.additionalMails || [])].map((mail: string) => (
                                <button
                                    key={mail}
                                    className={`text-left px-4 py-3 rounded-xl border text-sm font-medium transition-colors ${
                                        contactEmail === mail
                                            ? 'border-toola-500 bg-toola-500/10 text-toola-600'
                                            : 'border-gray-200 hover:border-toola-300 text-gray-700'
                                    }`}
                                    onClick={() => setContactEmail(mail)}
                                >
                                    {mail}
                                </button>
                            ))}
                        </div>
                        <button
                            className="mt-4 w-full bg-toola-500 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40"
                            disabled={!contactEmail}
                            onClick={() => {
                                if (contactEmail) {
                                    localStorage.setItem(`b2b_contact_${companyInfos.customerId}`, contactEmail);
                                    setShowEmailSelector(false);
                                }
                            }}
                        >
                            Confirmer
                        </button>
                    </div>
                </div>
            )}

            {/* Announcement modal */}
            <AnnounceModal
                isOpen={isOpen}
                onClose={onClose}
                description={companyInfos ? companyInfos.announcements : ''}
            />

            {/* Existing order modal */}
            <ExistingOrderModal
                isOpen={isOpenExistingOrder}
                onClose={onCloseExistingOrder}
                orders={existingOrders}
                selectedDate={pendingDate}
                onContinue={confirmDateChange}
                onEditOrder={handleEditOrder}
            />

            {selectedProductId ? (
                <ProductViewModal
                    isOpen={isOpenProductView}
                    onClose={() => {
                        onCloseProductView();
                        setSelectedProductId(null);
                    }}
                    product={findCachedProduct(selectedProductId as any)}
                    newOrder={newOrder}
                    hidePricesOnShop={companyInfos ? companyInfos.hidePricesOnShop : false}
                    showProductDetailsOnShop={companyInfos ? companyInfos.showProductDetailsOnShop : false}
                    onAddProduct={addNewItem}
                    onQuantityChange={handleQuantityChange}
                    selectedPackaging={selectedPackagings[selectedProductId]}
                    onPackagingChange={(packaging: any) => {
                        setSelectedPackagings((prev) => ({
                            ...prev,
                            [selectedProductId]: packaging || null
                        }));
                    }}
                />
            ) : null}

            {/* Header */}
            <div className={`fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200/50 shadow-sm transition-transform duration-300 ${
                showHeader ? 'translate-y-0' : '-translate-y-full'
            }`}>
                {/* Self-preview banner: owner browsing their own shop, ordering is disabled */}
                {isSelfPreview && (
                    <div className="bg-toola-500 text-white text-xs font-semibold text-center py-1.5 px-4">
                        Aperçu de votre boutique — les commandes sont désactivées
                    </div>
                )}
                {/* Blocked customer banner: browsing stays allowed, ordering is disabled */}
                {!isSelfPreview && conditions?.isBlocked && (
                    <div className="bg-red-600 text-white text-xs font-semibold text-center py-1.5 px-4">
                        {conditions.blockedMessage ||
                            'Votre compte est bloqué pour toute nouvelle commande.'}
                    </div>
                )}
                {/* Edit mode banner */}
                {editingOrderId && (
                    <div className="bg-amber-500 text-white text-xs font-semibold text-center py-1.5 px-4 flex items-center justify-center gap-2">
                        <span> Mode modification — vous modifiez une commande existante</span>
                        <button
                            onClick={() => {
                                setEditingOrderId(null);
                                setNewOrder({ items: [], totalPrice: 0 });
                                // Removing the cart also removes _editingOrderId
                                localStorage.removeItem(CART_STORAGE_KEY);
                            }}
                            className="underline ml-2 hover:no-underline"
                        >
                            Annuler
                        </button>
                    </div>
                )}
                <div className="max-w-7xl mx-auto px-4">
                    <div className="py-4">
                        <Header
                            companyInfos={companyInfos}
                            haveMultipleSupliers={haveMultipleSupliers}
                        />
                    </div>

                    {/* Search bar */}
                    {!confirmPage && (
                        <div className="pb-4 relative">
                            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
                            <input
                                type="text"
                                placeholder="Rechercher un produit..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-11 pr-4 py-3 bg-slate-100/50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm"
                            />
                        </div>
                    )}
                    
                    {/* Categories - attached to header */}
                    {companyInfos &&
                        !confirmPage &&
                        companyInfos.categories &&
                        companyInfos.categories.length > 0 && (
                            <div className="pb-4">
                                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                                    <button
                                        className={`flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap transition-all text-sm font-medium ${
                                            'all' === category
                                                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                                                : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
                                        }`}
                                        onClick={() => {
                                            setCategory('all');
                                            setSubCategory('all');
                                            setSubFamily('all');
                                        }}
                                    >
                                        Tout
                                    </button>
                                    {productsHaveFav() && (
                                        <button
                                            className={`flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap transition-all text-sm font-medium ${
                                                'fav' === category
                                                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                                                    : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
                                            }`}
                                            onClick={() => {
                                                setCategory('fav');
                                                setSubCategory('all');
                                                setSubFamily('all');
                                            }}
                                        >
                                            <FaHeart className="text-red-500" />
                                            Favoris
                                        </button>
                                    )}
                                    {companyInfos.categories
                                        .sort((a: any, b: any) => (a.position || 0) - (b.position || 0))
                                        .map((cat: any) => (
                                            <button
                                                key={cat.id}
                                                className={`flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap transition-all text-sm font-medium ${
                                                    cat.id === category
                                                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                                                        : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
                                                }`}
                                                onClick={() => {
                                                    setCategory(cat.id);
                                                    setSubCategory('all');
                                                    setSubFamily('all');
                                                }}
                                            >
                                                {icons.find((i: any) => i.name === cat.icon)?.icon || ''}
                                                {cat.name}
                                            </button>
                                        ))}
                                </div>
                                
                                {/* Sub-categories */}
                                {category !== 'all' && category !== 'fav' && (() => {
                                    const categorySubCategories = (companyInfos?.subCategories || [])
                                        .filter((sc: any) => sc.categoryId === category)
                                        .sort((a: any, b: any) => (a.position || 0) - (b.position || 0));
                                    
                                    if (categorySubCategories.length === 0) return null;
                                    
                                    return (
                                        <div className="mt-2 flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                                            <button
                                                className={`px-3 py-1.5 rounded-lg whitespace-nowrap transition-all text-xs font-medium ${
                                                    'all' === subCategory
                                                        ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}
                                                onClick={() => {
                                                    setSubCategory('all');
                                                    setSubFamily('all');
                                                }}
                                            >
                                                Toutes
                                            </button>
                                            {categorySubCategories.map((subCat: any) => (
                                                <button
                                                    key={subCat.id}
                                                    className={`px-3 py-1.5 rounded-lg whitespace-nowrap transition-all text-xs font-medium ${
                                                        subCat.name === subCategory
                                                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                    }`}
                                                    onClick={() => {
                                                        setSubCategory(subCat.name);
                                                        setSubFamily('all');
                                                    }}
                                                >
                                                    {subCat.name}
                                                </button>
                                            ))}
                                        </div>
                                    );
                                })()}

                                {/* Sub-families */}
                                {category !== 'all' && category !== 'fav' && subCategory !== 'all' && (() => {
                                    const selectedSubCat = (companyInfos?.subCategories || []).find(
                                        (sc: any) => sc.categoryId === category && sc.name === subCategory,
                                    );
                                    if (!selectedSubCat) return null;

                                    const subCatSubFamilies = (companyInfos?.subFamilies || [])
                                        .filter((sf: any) => sf.subCategoryId === selectedSubCat.id)
                                        .sort((a: any, b: any) => (a.position || 0) - (b.position || 0));

                                    if (subCatSubFamilies.length === 0) return null;

                                    return (
                                        <div className="mt-2 flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                                            <button
                                                className={`px-3 py-1.5 rounded-lg whitespace-nowrap transition-all text-xs font-medium ${
                                                    'all' === subFamily
                                                        ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}
                                                onClick={() => setSubFamily('all')}
                                            >
                                                Toutes
                                            </button>
                                            {subCatSubFamilies.map((subFam: any) => (
                                                <button
                                                    key={subFam.id}
                                                    className={`px-3 py-1.5 rounded-lg whitespace-nowrap transition-all text-xs font-medium ${
                                                        subFam.name === subFamily
                                                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                    }`}
                                                    onClick={() => setSubFamily(subFam.name)}
                                                >
                                                    {subFam.name}
                                                </button>
                                            ))}
                                        </div>
                                    );
                                })()}
                            </div>
                        )}
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 pt-[210px]">
                {/* Sort + View toggle */}
                {!confirmPage && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setSortBy(sortBy === 'az' ? 'default' : 'az')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                sortBy === 'az'
                                    ? 'bg-[#01499b] text-white border-[#01499b]'
                                    : 'bg-white text-slate-700 border-slate-200 hover:border-[#01499b] hover:text-[#01499b]'
                            }`}
                        >
                            <span>A → Z</span>
                        </button>
                        <button
                            onClick={() => setSortBy(sortBy === 'price-asc' ? 'price-desc' : 'price-asc')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                sortBy === 'price-asc' || sortBy === 'price-desc'
                                    ? 'bg-[#01499b] text-white border-[#01499b]'
                                    : 'bg-white text-slate-700 border-slate-200 hover:border-[#01499b] hover:text-[#01499b]'
                            }`}
                        >
                            <span>Prix {sortBy === 'price-desc' ? '↑' : '↓'}</span>
                        </button>
                        <div className="ml-auto flex items-center border border-slate-200 rounded-lg overflow-hidden">
                            <button
                                onClick={() => setViewMode('grid')}
                                title="Vue grille"
                                className={`p-1.5 transition-colors ${
                                    viewMode === 'grid' ? 'bg-[#01499b] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                                }`}
                            >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                title="Vue liste"
                                className={`p-1.5 transition-colors ${
                                    viewMode === 'list' ? 'bg-[#01499b] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                                }`}
                            >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" /></svg>
                            </button>
                        </div>
                    </div>
                )}

                {!confirmPage && !lastOrderAdded && lastOrder && lastOrder.items && lastOrder.items.length > 0 && (
                    <div className="mt-2">
                        <button
                            onClick={addLastOrderToCart}
                            disabled={loadingLastOrder}
                            className="bg-transparent border border-[#01499b] hover:bg-[#01499b]/5 text-[#01499b] px-3 py-1 rounded-lg transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loadingLastOrder ? (
                                <>
                                    <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span className="text-xs">Ajout en cours...</span>
                                </>
                            ) : (
                                <span className="text-xs font-medium">Pré-remplir par l'IA ({lastOrder.items.length} produits)</span>
                            )}
                        </button>
                    </div>
                )}
                
                {/* Order details */}
                    {confirmPage && (
                        <div className="relative py-6">
                            <button
                                className="mb-4 text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                                onClick={() => setConfirmPage(false)}
                            >
                                {'← Retour à la boutique B2B'}
                            </button>
                            
                            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
                                <div className="p-6 border-b border-slate-200">
                                    <h2 className="text-2xl font-bold text-slate-800 mb-2">Finaliser la commande</h2>
                                </div>
                    
                                <div className="p-6">
                                    <h3 className="text-xl font-bold text-slate-800 mb-4">Récapitulatif de la commande</h3>
                                    {newOrder.items.length > 0 ? (
                                        <div className="space-y-3">
                                            {newOrder.items.map((item: any) => {
                                                // Find the product to get packagings
                                                const product = findCachedProduct(item.productId);

                                                const hasPackagings = product?.packagings && product.packagings.length > 0;
                                                
                                                return (
                                                <div key={item.productId} className="p-3 sm:p-4 bg-slate-50 rounded-xl">
                                                    {/* Product name at the top on mobile*/}
                                                    <div className="flex items-start justify-between gap-3 mb-3">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-medium text-slate-800 text-sm sm:text-base">{item.name}</p>
                                                            <p className="text-xs sm:text-sm text-slate-500 mt-1">
                                                                {item.packagingUnity || item.unity}
                                                            </p>
                                                            {!companyInfos?.hidePricesOnShop && !item.packagingUnity && product?.subUnitQuantity > 0 && product?.subUnity && (
                                                                <p className="text-xs text-slate-400 mt-0.5">
                                                                    Prix au {product.subUnity}: {(getItemDisplayPrice(item) / product.subUnitQuantity).toFixed(2)} €
                                                                </p>
                                                            )}
                                                        </div>

                                                        <button
                                                            onClick={() => removeItem(item.productId)}
                                                            className="p-2 hover:bg-red-50 rounded-lg text-red-500 transition-colors flex-shrink-0"
                                                        >
                                                            <RxCross2 size={18} />
                                                        </button>
                                                    </div>
                                                    
                                                    {/* Packaging selector */}
                                                    {hasPackagings && (() => {
                                                        const b2bOnlyPkgs = product.packagings.filter((p: any) => p.b2bOnly);
                                                        const effectivePkgs = b2bOnlyPkgs.length > 0 ? b2bOnlyPkgs : product.packagings;
                                                        const hideBase = b2bOnlyPkgs.length > 0;
                                                        return (
                                                        <div className="mb-3">
                                                            <label className="text-xs font-medium text-slate-600 mb-1 block">
                                                                Conditionnement
                                                            </label>
                                                            <select
                                                                value={item.packagingUnity || ''}
                                                                onChange={(e) => {
                                                                    const selectedUnity = e.target.value;
                                                                    if (!selectedUnity) {
                                                                        handlePackagingChangeInCart(item.productId, null);
                                                                    } else {
                                                                        const packaging = effectivePkgs.find(
                                                                            (p: any) => p.unity === selectedUnity
                                                                        );
                                                                        handlePackagingChangeInCart(item.productId, packaging);
                                                                    }
                                                                }}
                                                                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                                            >
                                                                {!hideBase && (
                                                                    <option value="">
                                                                        Unité de base ({product.unity})
                                                                    </option>
                                                                )}
                                                                {effectivePkgs.map((pkg: any, idx: number) => (
                                                                    <option key={idx} value={pkg.unity}>
                                                                        {pkg.unity} - {parseFloat(pkg.price).toFixed(2)}€
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        );
                                                    })()}
                                                    
                                                    {/* Button to display comment field */}
                                                    <div className="mb-3">
                                                        <button
                                                            onClick={() => toggleCommentField(item.productId)}
                                                            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                                                        >
                                                            {openCommentFields[item.productId] ? '− Masquer le commentaire' : '+ Ajouter un commentaire'}
                                                        </button>
                                                        
                                                        {openCommentFields[item.productId] && (
                                                            <textarea
                                                                value={item.comment || ''}
                                                                onChange={(e) => handleCommentChange(item.productId, e.target.value)}
                                                                placeholder="Ajouter un commentaire pour ce produit..."
                                                                className="w-full px-3 py-2 mt-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white resize-none"
                                                                rows={2}
                                                            />
                                                        )}
                                                    </div>
                    
                                                    {/* Quantity controls and price at the bottom */}
                                                    <div className="flex items-center justify-between gap-3">
                                                        {/* Quantity controls */}
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => handleQuantityChange(Math.max(0, item.quantity - 1), item.productId)}
                                                                className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-white hover:bg-slate-200 rounded-lg border border-slate-300 transition-colors"
                                                            >
                                                                <AiOutlineMinus size={14} className="text-slate-700" />
                                                            </button>
                                                            <input
                                                                type="number"
                                                                value={item.quantity}
                                                                onChange={(e) => {
                                                                    const val = parseFloat(e.target.value);
                                                                    if (!isNaN(val) && val >= 0) {
                                                                        handleQuantityChange(val, item.productId);
                                                                    }
                                                                }}
                                                                className="w-14 sm:w-16 h-8 sm:h-9 text-center font-semibold text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                            />
                                                            <button
                                                                onClick={() => handleQuantityChange(item.quantity + 1, item.productId)}
                                                                className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                                                            >
                                                                <AiOutlinePlus size={14} className="text-white" />
                                                            </button>
                                                        </div>
                    
                                                        {/* Price */}
                                                        {!companyInfos?.hidePricesOnShop && (
                                                            <p className="text-base sm:text-lg font-bold text-blue-600 whitespace-nowrap">
                                                                {(getItemDisplayPrice(item) * item.quantity).toFixed(2)} €
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            )})}

                                            {!companyInfos?.hidePricesOnShop && (
                                                <div className="pt-4 border-t border-slate-200 flex justify-between items-center">
                                                    <p className="text-base sm:text-lg font-semibold text-slate-800">Total HT</p>
                                                    <p className="text-xl sm:text-2xl font-bold text-blue-600">
                                                        {newOrder.items.reduce((sum: number, item: any) => sum + getItemDisplayPrice(item) * item.quantity, 0).toFixed(2)}€
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-center py-8 text-slate-500">
                                            Votre commande ne contient aucun produit
                                        </p>
                                    )}
                                </div>
                    
                                {/* Recommendations Section - Upsell */}
                                {newOrder.items.length > 0 && (() => {
                                    const recommendedProducts = recommendationPool
                                        .filter((p: any) => !newOrder.items.find((item: any) => item.productId === p.id))
                                        .sort((a: any, b: any) => getOrderCount(b.id) - getOrderCount(a.id))
                                        .slice(0, 4);
                                    
                                    if (recommendedProducts.length === 0) return null;
                                    
                                    return (
                                    <div className="p-6 border-t border-slate-200 bg-gradient-to-br from-blue-50/50 to-slate-50">
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                                                <span className="text-white text-lg">✨</span>
                                            </div>
                                            <h3 className="text-base sm:text-lg font-bold text-slate-800">
                                                Nos clients ont aussi commandé
                                            </h3>
                                        </div>
                                        
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            {recommendedProducts.map((product: any) => {
                                                    const inCart = newOrder.items.find((i: any) => i.productId === product.id);
                                                    return (
                                                        <div
                                                            key={product.id}
                                                            className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-blue-300 hover:shadow-lg transition-all group"
                                                        >
                                                            <div className="relative h-24 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
                                                                <img
                                                                    className="max-h-16 object-contain group-hover:scale-110 transition-transform duration-300"
                                                                    src={
                                                                        Array.isArray(product.imageUrls) && product.imageUrls.length > 0
                                                                            ? product.imageUrls[0]
                                                                            : product.imageUrl || getIcon(product.name, 'product')
                                                                    }
                                                                    alt={product.name}
                                                                    onError={(e: any) => {
                                                                        e.target.src = getIcon(product.name, 'product');
                                                                    }}
                                                                />
                                                                
                                                                {getOrderCount(product.id) > 10 && (
                                                                    <div className="absolute top-1 right-1 bg-red-500 text-white px-1.5 py-0.5 rounded text-xs font-bold">
                                                                        ❤️
                                                                    </div>
                                                                )}
                                                            </div>
                    
                                                            <div className="p-3">
                                                                <h4 className="font-semibold text-slate-800 text-sm line-clamp-2 mb-1 min-h-[2.5rem]">
                                                                    {product.name}
                                                                </h4>
                                                                
                                                                {!companyInfos?.hidePricesOnShop && (
                                                                    <div className="flex items-baseline gap-1 mb-2">
                                                                        <span className="text-lg font-bold text-blue-600">
                                                                            {product.price.toFixed(2)}€
                                                                        </span>
                                                                        <span className="text-xs text-slate-500">/ {product.unity}</span>
                                                                    </div>
                                                                )}
                    
                                                                {!inCart ? (
                                                                    <button
                                                                        onClick={() => addNewItem(product.id, product.name, 1)}
                                                                        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-2 rounded-lg text-sm font-medium transition-all hover:shadow-md flex items-center justify-center gap-1"
                                                                    >
                                                                        <AiOutlinePlus size={14} />
                                                                        <span>Ajouter</span>
                                                                    </button>
                                                                ) : (
                                                                    <div className="flex items-center gap-1">
                                                                        <button
                                                                            onClick={() => handleQuantityChange(inCart.quantity - 1, product.id)}
                                                                            className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-1.5 rounded-lg transition-colors"
                                                                        >
                                                                            <AiOutlineMinus className="w-3 h-3 mx-auto" />
                                                                        </button>
                                                                        <div className="flex-1 text-center font-bold text-sm">
                                                                            {inCart.quantity}
                                                                        </div>
                                                                        <button
                                                                            onClick={() => handleQuantityChange(inCart.quantity + 1, product.id)}
                                                                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-1.5 rounded-lg transition-colors"
                                                                        >
                                                                            <AiOutlinePlus className="w-3 h-3 mx-auto" />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                        </div>
                    
                                        <div className="mt-4 p-3 bg-blue-100/50 border border-blue-200 rounded-xl">
                                            <p className="text-sm text-blue-900 text-center">
                                                💡 <span className="font-semibold">Astuce :</span> Complétez votre commande pour optimiser votre livraison
                                            </p>
                                        </div>
                                    </div>
                                    );
                                })()}
                    
                                {/* Delivery information */}
                                <div className="p-6 bg-slate-50 border-t border-slate-200">
                                    <h3 className="text-lg font-bold text-slate-800 mb-4">Informations de livraison</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <InputField
                                                label="Date de livraison*"
                                                id="deliveryDate"
                                                type="date"
                                                min={new Date().toISOString().split('T')[0]}
                                                onChange={handleDateChange}
                                                inputSmall={true}
                                            />
                                            {dateError && (
                                                <p className="mt-2 text-red-500 text-xs">{dateError}.</p>
                                            )}
                                        </div>
                                        <div>
                                            <InputField
                                                label="Information livreur"
                                                placeholder="Porte 2, code 7678"
                                                id="customerNotes"
                                                type="text"
                                                onChange={handleChange}
                                                inputSmall={true}
                                            />
                                        </div>
                                    </div>
                    
                                    <button
                                        className={`w-full mt-6 py-4 rounded-xl text-sm font-semibold transition-all ${
                                            newOrder.items.length > 0 &&
                                            newOrder.deliveryDate &&
                                            (conditions && conditions.minimumOrderAmount
                                                ? newOrder.totalPrice >= conditions.minimumOrderAmount
                                                : true) &&
                                            !disableSendButton &&
                                            !isSelfPreview &&
                                            !conditions?.isBlocked
                                                ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl'
                                                : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                                        }`}
                                        disabled={disableSendButton || isSelfPreview || conditions?.isBlocked}
                                        onClick={() => {
                                            if (disableSendButton || isSubmitting.current || isSelfPreview || conditions?.isBlocked) {
                                                return;
                                            }
                                            if (newOrder.items.length > 0 && newOrder.deliveryDate) {
                                                sendOrder();
                                            }
                                        }}
                                    >
                                        {isSelfPreview
                                            ? 'Aperçu — commande désactivée'
                                            : conditions?.isBlocked
                                                ? 'Compte bloqué — commande impossible'
                                                : disableSendButton
                                                    ? 'Envoi en cours...'
                                                    : 'Envoyer la commande'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                {confirmPage === false && (
                    <>
                        {/* Products grid */}
                        <div className="mt-4 pb-32">
                            <div className={viewMode === 'list' ? 'flex flex-col gap-3' : 'grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4'}>
                                {isFullyLoaded
                                    ? visibleProducts
                                          .map((product: any, index: number) => {
                                              // If product is out of stock and has replacement products
                                              if (product.isOutOfStock && product.hasReplacement && product.replacementProducts?.length > 0) {
                                                  const isExpanded = expandedReplacements[product.id] || false;
                                                  const maxVisible = 2;
                                                  const displayedReplacements = isExpanded 
                                                      ? product.replacementProducts 
                                                      : product.replacementProducts.slice(0, maxVisible);
                                                  const hasMore = product.replacementProducts.length > maxVisible;
                                                  
                                                  return (
                                                      <div key={index} className="col-span-1">
                                                          <div className="bg-orange-50 border-2 border-orange-200 rounded-xl shadow-md hover:shadow-lg transition-shadow flex flex-col h-full">
                                                              <div className="p-3 flex-shrink-0">
                                                                  <p className="text-sm font-bold text-orange-800 mb-1 line-clamp-2 min-h-[2.5rem]">
                                                                      🔴 {product.name}
                                                                  </p>
                                                                  <p className="text-xs text-orange-700 mb-2">
                                                                      Rupture de stock
                                                                  </p>
                                                                  <p className="text-xs font-semibold text-orange-900 mb-2">
                                                                      Produits de remplacement :
                                                                  </p>
                                                              </div>
                                                              <div className="flex-1 overflow-y-auto max-h-[280px] px-3 pb-3 space-y-2 scrollbar-thin scrollbar-thumb-orange-300 scrollbar-track-orange-100">
                                                                  {displayedReplacements.map((replacement: any, rpIdx: number) => (
                                                                      <div key={rpIdx} className="bg-white rounded-lg p-2 hover:shadow-md transition-shadow cursor-pointer border border-orange-200"
                                                                           onClick={() => {
                                                                               setSelectedProductId(replacement.id);
                                                                               onOpenProductView();
                                                                           }}>
                                                                          <div className="flex items-center gap-2">
                                                                              <div className="flex-1 min-w-0">
                                                                                  <p className="text-sm font-medium text-gray-900 line-clamp-2">{replacement.name}</p>
                                                                                  {!companyInfos?.hidePricesOnShop && (
                                                                                      <p className="text-xs text-gray-600 mt-0.5">{replacement.price?.toFixed(2)} € / {replacement.unity}</p>
                                                                                  )}
                                                                              </div>
                                                                              <button
                                                                                  onClick={(e) => {
                                                                                      e.stopPropagation();
                                                                                      addNewItem(replacement.id, replacement.name, 1);
                                                                                  }}
                                                                                  className="px-2 py-1 bg-green-500 text-white rounded-full text-xs font-semibold hover:bg-green-600 transition-colors flex-shrink-0 shadow-sm"
                                                                              >
                                                                                  +
                                                                              </button>
                                                                          </div>
                                                                      </div>
                                                                  ))}
                                                                  
                                                                  {hasMore && (
                                                                      <button
                                                                          onClick={() => {
                                                                              setExpandedReplacements(prev => ({
                                                                                  ...prev,
                                                                                  [product.id]: !prev[product.id]
                                                                              }));
                                                                          }}
                                                                          className="w-full py-2 text-xs font-semibold text-orange-700 hover:text-orange-900 hover:bg-orange-100 rounded-lg transition-colors flex items-center justify-center gap-1"
                                                                      >
                                                                          {isExpanded ? (
                                                                              <>
                                                                                  <FaChevronUp size={10} />
                                                                                  Voir moins
                                                                              </>
                                                                          ) : (
                                                                              <>
                                                                                  <FaChevronDown size={10} />
                                                                                  Voir {product.replacementProducts.length - maxVisible} autre(s) produit(s)
                                                                              </>
                                                                          )}
                                                                      </button>
                                                                  )}
                                                              </div>
                                                          </div>
                                                      </div>
                                                  );
                                              }
                                              
                                              // Normal Product
                                              if (viewMode === 'list') {
                                                  const inCart = newOrder.items.find((i: any) => i.productId === product.id);
                                                  const hasPackagings = product.packagings && product.packagings.length > 0;
                                                  const b2bOnlyPkgs = hasPackagings ? product.packagings.filter((p: any) => p.b2bOnly) : [];
                                                  const effectiveListPkgs = b2bOnlyPkgs.length > 0 ? b2bOnlyPkgs : (product.packagings || []);
                                                  const hideListBase = b2bOnlyPkgs.length > 0;
                                                  const currentPrice = companyInfos && companyInfos.hidePricesOnShop ? null : (selectedPackagings[product.id]?.price || product.price);
                                                  const orderedCount = getOrderCount(product.id);
                                                  const isLiked = likedProductIds.includes(product.id);
                                                  return (
                                                      <div key={index} className={`flex items-center gap-3 bg-white rounded-xl border px-3 py-2 hover:border-blue-300 hover:shadow-sm transition-all ${inCart ? 'border-toola-400 bg-toola-400/5' : 'border-slate-200'}`}>
                                                          <img
                                                              className="w-12 h-12 object-contain rounded-lg flex-shrink-0"
                                                              src={
                                                                  Array.isArray(product.imageUrls) && product.imageUrls.length > 0
                                                                      ? product.imageUrls[0]
                                                                      : product.imageUrl || getIcon(product.name, 'product')
                                                              }
                                                              alt={product.name}
                                                              onError={(e: any) => { e.target.src = getIcon(product.name, 'product'); }}
                                                          />
                                                          <div className="flex-1 min-w-0">
                                                              <div className="flex items-center gap-1.5">
                                                                  <p className="text-sm font-medium text-slate-800 truncate">{product.name}</p>
                                                                  {orderedCount > 0 && (
                                                                      <span className="flex items-center gap-0.5 text-xs text-pink-500 font-medium flex-shrink-0">
                                                                          <FaHeart size={10} />
                                                                          {orderedCount}x
                                                                      </span>
                                                                  )}
                                                              </div>
                                                              {currentPrice > 0 && (
                                                                  <p className="text-xs text-blue-600 font-semibold">{currentPrice.toFixed(2)} € / {selectedPackagings[product.id]?.unity || product.unity}</p>
                                                              )}
                                                              {hasPackagings && (
                                                                  <select
                                                                      className="mt-1 w-full max-w-[180px] px-2 py-0.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                                      value={selectedPackagings[product.id]?.unity || ''}
                                                                      onChange={(e) => {
                                                                          if (e.target.value === '') {
                                                                              setSelectedPackagings((prev) => ({ ...prev, [product.id]: null }));
                                                                          } else {
                                                                              const packaging = effectiveListPkgs.find((p: any) => p.unity === e.target.value);
                                                                              setSelectedPackagings((prev) => ({ ...prev, [product.id]: packaging || null }));
                                                                          }
                                                                      }}
                                                                  >
                                                                      {!hideListBase && <option value="">Unité de base ({product.unity})</option>}
                                                                      {effectiveListPkgs.map((pkg: any, idx: number) => (
                                                                          <option key={idx} value={pkg.unity}>{pkg.unity} — {parseFloat(pkg.price).toFixed(2)}€</option>
                                                                      ))}
                                                                  </select>
                                                              )}
                                                          </div>
                                                          {/* Like button */}
                                                          <button
                                                              className="p-1.5 rounded-full transition-colors hover:bg-pink-50 flex-shrink-0"
                                                              onClick={() => handleToggleLike(product.id)}
                                                              title={isLiked ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                                                          >
                                                              <FaHeart size={14} className={isLiked ? 'text-pink-500' : 'text-gray-300'} />
                                                          </button>
                                                          <div className="flex items-center gap-1.5 flex-shrink-0">
                                                              {inCart ? (
                                                                  <>
                                                                      <button onClick={() => handleQuantityChange(Math.max(0, inCart.quantity - 1), product.id)} className="w-7 h-7 flex items-center justify-center bg-slate-100 hover:bg-slate-200 rounded-lg border border-slate-300 transition-colors">
                                                                          <AiOutlineMinus size={12} className="text-slate-700" />
                                                                      </button>
                                                                      <span className="w-7 text-center text-sm font-semibold">{inCart.quantity}</span>
                                                                      <button onClick={() => handleQuantityChange(inCart.quantity + 1, product.id)} className="w-7 h-7 flex items-center justify-center bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
                                                                          <AiOutlinePlus size={12} className="text-white" />
                                                                      </button>
                                                                  </>
                                                              ) : (
                                                                  <button onClick={() => addNewItem(product.id, product.name, 1)} className="w-7 h-7 flex items-center justify-center bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
                                                                      <AiOutlinePlus size={12} className="text-white" />
                                                                  </button>
                                                              )}
                                                          </div>
                                                      </div>
                                                  );
                                              }
                                              return (
                                                  <ProductCard
                                                      id={product.id}
                                                      key={index}
                                                      name={product.name}
                                                      imageUrl={product.imageUrl}
                                                      type={product.type}
                                                      alert={product.alert}
                                                      img={getIcon(product.name, 'product')}
                                                      imageUrls={
                                                        Array.isArray(product.imageUrls) && product.imageUrls.length > 0
                                                        ? product.imageUrls
                                                        : product.imageUrl
                                                            ? [product.imageUrl]
                                                            : []
                                                      }
                                                      unity={product.unity}
                                                      price={companyInfos && companyInfos.hidePricesOnShop ? 0 : (selectedPackagings[product.id]?.price || product.price)}
                                                      hidePricesOnShop={companyInfos ? companyInfos.hidePricesOnShop : false}
                                                      subUnitQuantity={product.subUnitQuantity}
                                                      subUnity={product.subUnity}
                                                      packagings={product.packagings}
                                                      selectedPackaging={selectedPackagings[product.id]}
                                                      onPackagingChange={(packaging: any) => {
                                                          setSelectedPackagings((prev) => ({
                                                              ...prev,
                                                              [product.id]: packaging || null
                                                          }));
                                                      }}
                                                      onAddProduct={addNewItem}
                                                      onQuantityChange={handleQuantityChange}
                                                      quantity={
                                                          newOrder.items.find(
                                                              (i: any) => i.productId === product.id,
                                                          )?.quantity
                                                      }
                                                      orderedCount={getOrderCount(product.id)}
                                                      remove={removeItem}
                                                      order={newOrder}
                                                      isLiked={likedProductIds.includes(product.id)}
                                                      onToggleLike={handleToggleLike}
                                                      onClick={() => {
                                                          setSelectedProductId(product.id);
                                                          onOpenProductView();
                                                      }}
                                                  />
                                              );
                                          })
                                    : Array(productsPerLoad)
                                          .fill(<ProductLoadingCard />)
                                          .map((product: any, i: number) => (
                                              <ProductLoadingCard key={i} />
                                          ))}
                            </div>

                            {isFullyLoaded && visibleProducts.length === 0 && (
                                <p className="mt-10 text-center text-sm text-slate-500">
                                    {search
                                        ? 'Aucun produit ne correspond à votre recherche.'
                                        : 'Aucun produit dans cette catégorie.'}
                                </p>
                            )}

                            {isFullyLoaded && autoOrderProducts.length < autoOrderProductsMeta.total && (
                                <div className="mt-6 text-center">
                                    <div className="inline-block">
                                        <svg
                                            className="w-8 h-8 text-blue-600 animate-spin"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            />
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            />
                                        </svg>
                                    </div>
                                    <p className="mt-2 text-sm text-slate-500">Chargement des produits...</p>
                                </div>
                            )}
                        </div>

                        {/* Cart summary card */}
                        <div className="fixed bottom-0 left-0 right-0 z-50">
                            <div className="max-w-7xl mx-auto px-4 pb-4">
                                <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
                                    <div
                                        onClick={() => setShowOrderSummary(!showOrderSummary)}
                                        className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="relative">
                                                <FaShoppingCart className="w-6 h-6 text-blue-600" />
                                                {newOrder.items.length > 0 && (
                                                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                                                        {newOrder.items.length}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-left">
                                                <p className="text-sm font-semibold text-slate-800">
                                                    {newOrder.items.length} produit{newOrder.items.length !== 1 ? 's' : ''}
                                                </p>
                                                {!companyInfos || !companyInfos.hidePricesOnShop ? (
                                                    <p className="text-xs text-slate-500">
                                                        Total HT: <span className="font-bold text-blue-600">{newOrder.items.reduce((sum: number, item: any) => sum + getItemDisplayPrice(item) * item.quantity, 0).toFixed(2)}€</span>
                                                    </p>
                                                ) : null}
                                                {conditions && conditions.minimumOrderAmount > 0 && (() => {
                                                    const progress = Math.min((newOrder.totalPrice / conditions.minimumOrderAmount) * 100, 100);
                                                    const reached = newOrder.totalPrice >= conditions.minimumOrderAmount;
                                                    const remaining = (conditions.minimumOrderAmount - newOrder.totalPrice).toFixed(2);
                                                    return (
                                                        <div className="mt-1 w-40">
                                                            <div className="flex justify-between items-center mb-0.5">
                                                                <span className={`text-xs font-medium ${reached ? 'text-green-600' : 'text-orange-500'}`}>
                                                                    {reached ? '✓ Minimum atteint' : `${remaining}€ manquants`}
                                                                </span>
                                                            </div>
                                                            <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                                                <div
                                                                    className={`h-full rounded-full transition-all duration-500 ${reached ? 'bg-green-500' : 'bg-orange-400'}`}
                                                                    style={{ width: `${progress}%` }}
                                                                />
                                                            </div>
                                                            <p className="text-[10px] text-slate-400 mt-0.5">Min. {conditions.minimumOrderAmount}€</p>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            {showOrderSummary ? (
                                                <FaChevronDown className="w-4 h-4 text-slate-600" />
                                            ) : (
                                                <FaChevronUp className="w-4 h-4 text-slate-600" />
                                            )}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (
                                                        newOrder.items.length > 0 &&
                                                        (conditions && conditions.minimumOrderAmount
                                                            ? newOrder.totalPrice >= conditions.minimumOrderAmount
                                                            : true)
                                                    ) {
                                                        setConfirmPage(true);
                                                    }
                                                }}
                                                className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                                                    newOrder.items.length > 0 &&
                                                    (conditions && conditions.minimumOrderAmount
                                                        ? newOrder.totalPrice >= conditions.minimumOrderAmount
                                                        : true)
                                                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl'
                                                        : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                                                }`}
                                            >
                                                Commander
                                            </button>
                                        </div>
                                    </div>

                                    {/* Details of the expandable cart */}
                                {showOrderSummary && newOrder.items.length > 0 && (
                                    <div className="border-t border-slate-200 max-h-96 overflow-y-auto">
                                        {newOrder.items.map((item: any) => (
                                            <div
                                                key={item.productId}
                                                className="p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0"
                                            >
                                                <div className="flex-1">
                                                    <p className="font-medium text-slate-800 text-sm">{item.name}</p>
                                                    <p className="text-xs text-slate-500">
                                                        {item.quantity} {item.unity}
                                                    </p>
                                                </div>
                                                {!companyInfos?.hidePricesOnShop && (
                                                    <p className="font-bold text-blue-600 text-sm">
                                                        {(item.quantity * getItemDisplayPrice(item)).toFixed(2)}€
                                                    </p>
                                                )}
                                                <button
                                                    onClick={() => removeItem(item.productId)}
                                                    className="p-2 hover:bg-red-50 rounded-lg text-red-500 transition-colors"
                                                >
                                                    <RxCross2 size={18} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    </div>
);
};

export default CommandInfo;